/* eslint-disable prefer-arrow-callback */
import { type Dispatch, type MutableRefObject, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { getUserFacingApiErrorMessage, productAnalytics, useMutateSoilSightOutcomes, useTranslation } from "utils";

import { CurrencyCode, GET_SCENARIO_RESILIENCE_SCORE } from "consts";

import type { SoilSightKeyInsight } from "models";

import { Message } from "components";

import type { RecalcValidation } from "pages/app/scenario-analysis/hooks/useRecalcValidation";
import type { ScrollIntents } from "pages/app/scenario-analysis/hooks/useScrollIntents";
import type { SoilSightPersistence } from "pages/app/scenario-analysis/hooks/useSoilSightPersistence";
import { type ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { type SoilSightOutcomesByScenario, areOutcomesStale } from "pages/app/scenario-analysis/utils/forecast";
import { getScenariosWithProjections, reconcileKeyInsight } from "pages/app/scenario-analysis/utils/keyInsightsHelpers";
import { watchProjectSwitch } from "pages/app/scenario-analysis/utils/projectSwitch";
import { isDeviceOfflineError, waitUntilIdle } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { type ForecastFailReason, type ForecastTrigger, classifyForecastError } from "pages/app/scenario-analysis/utils/soilSightTracking";

const trackForecastFail = (reason: ForecastFailReason) => productAnalytics.track("soilsights:forecast_fail", { reason });

type ScenarioRecalcArgs = {
  scenarios: ScenarioData[];
  isDirty: boolean;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
  hasCalculated: boolean;
  setHasCalculated: Dispatch<SetStateAction<boolean>>;
  awaitingInitialCalc: boolean;
  setAwaitingInitialCalc: Dispatch<SetStateAction<boolean>>;
  projectQuery: { isSuccess: boolean; data?: { locationRegion?: string | null } };
  ensureProjectId: () => Promise<string | null>;
  projectEpochRef: MutableRefObject<number>;
  persistence: Pick<SoilSightPersistence, "flushUnsavedChanges" | "getRunInputsSnapshot" | "pruneAndPersistEmptyYears" | "isSavingCount" | "isSavingRef">;
  recalcValidation: Pick<RecalcValidation, "passesValidation" | "clearValidationErrors">;
  dirtyBeforeTabEditsRef: MutableRefObject<Map<string, boolean>>;
  scrollIntents: Pick<ScrollIntents, "requestChartScrollForManualRecalc" | "settleLandingRecalc">;
  fieldReselectNeeded: boolean;
  openFieldReselect: () => void;
  timelineYears: number;
};

/** Running the forecast: the validated, settings and background runs, and their results. */
export const useScenarioRecalc = ({
  scenarios,
  isDirty,
  setIsDirty,
  hasCalculated,
  setHasCalculated,
  awaitingInitialCalc,
  setAwaitingInitialCalc,
  projectQuery,
  ensureProjectId,
  projectEpochRef,
  persistence,
  recalcValidation,
  dirtyBeforeTabEditsRef,
  scrollIntents,
  fieldReselectNeeded,
  openFieldReselect,
  timelineYears,
}: ScenarioRecalcArgs) => {
  const { t, i18n } = useTranslation("scenarioAnalysis");
  const queryClient = useQueryClient();
  const { flushUnsavedChanges, getRunInputsSnapshot, pruneAndPersistEmptyYears, isSavingCount, isSavingRef } = persistence;
  const { passesValidation, clearValidationErrors } = recalcValidation;
  const { requestChartScrollForManualRecalc, settleLandingRecalc } = scrollIntents;

  const [isCalculating, setIsCalculating] = useState(false);
  const [isSettingsRunPending, setIsSettingsRunPending] = useState(false);
  // Runs asked for and not yet finished, counting the validation and save before isCalculating turns on.
  const [pendingRunRequests, setPendingRunRequests] = useState(0);
  const [soilSightOutcomes, setSoilSightOutcomes] = useState<SoilSightOutcomesByScenario | null>(null);
  const [keyInsight, setKeyInsight] = useState<SoilSightKeyInsight | null>(null);
  const [currency, setCurrency] = useState<CurrencyCode | null>(null);
  const firstCalculationSettledRef = useRef(false);
  const recalculateInFlightRef = useRef(false);
  // Read after awaits, so a run that fails because the field went missing meanwhile still names the cause.
  const fieldReselectNeededRef = useRef(fieldReselectNeeded);
  fieldReselectNeededRef.current = fieldReselectNeeded;
  // We read this when the run lands. A settings-bar pick starts the run before the render with its timeline.
  const timelineYearsRef = useRef(timelineYears);
  timelineYearsRef.current = timelineYears;

  // A run's key insight is dropped once the scenarios no longer match it (e.g. its best scenario was deleted).
  const reconciledKeyInsight = useMemo(() => reconcileKeyInsight(keyInsight, scenarios), [keyInsight, scenarios]);
  useEffect(
    function clearStaleKeyInsight() {
      if (keyInsight && !reconciledKeyInsight) setKeyInsight(null);
    },
    [keyInsight, reconciledKeyInsight]
  );

  // Errors get one context-specific message below, not the global toast as well.
  const { mutateAsync: mutateGenerateSoilSightOutcomes } = useMutateSoilSightOutcomes({ hideErrorMessage: true });

  // The project epoch a run was asked for while one was out (that run read the server before these inputs were saved), so it runs once more on that project.
  const rerunRequestedEpochRef = useRef<number | null>(null);
  const rerunTriggerRef = useRef<ForecastTrigger>("manual");
  const handleRecalculateRef = useRef<(trigger: ForecastTrigger) => Promise<void>>(() => Promise.resolve());

  const blockOnFieldReselect = useCallback(
    function blockOnFieldReselect(): boolean {
      if (!fieldReselectNeededRef.current) return false;
      trackForecastFail("field_reselect");
      openFieldReselect();
      return true;
    },
    [openFieldReselect]
  );

  const runRecalculation = useCallback(
    async function runRecalculation(trigger: ForecastTrigger) {
      if (blockOnFieldReselect()) {
        setIsSettingsRunPending(false);
        return;
      }
      const isSameProject = watchProjectSwitch(projectEpochRef);
      // The run reads inputs from the server, so pending edits are saved first; a failed save is toasted and the run proceeds.
      const isSaved = await flushUnsavedChanges();
      await waitUntilIdle(isSavingRef);
      // The user left the project while its edits were saving; the next project's run is its own.
      if (!isSameProject()) {
        setIsSettingsRunPending(false);
        return;
      }

      if (recalculateInFlightRef.current) {
        // One rerun serves every queued request. A click must not be relabelled as an automatic run.
        if (rerunRequestedEpochRef.current !== projectEpochRef.current || trigger === "manual") rerunTriggerRef.current = trigger;
        rerunRequestedEpochRef.current = projectEpochRef.current;
        return;
      }
      recalculateInFlightRef.current = true;
      setIsCalculating(true);
      setAwaitingInitialCalc(false);

      try {
        const id = await ensureProjectId();
        const projectCountry = projectQuery.data?.locationRegion?.trim();
        if (projectQuery.isSuccess && !projectCountry) {
          trackForecastFail("missing_country");
          Message({ content: t("outcomeComparison.missingProjectCountry"), icon: "error" });
          return;
        }
        const inputsAtRun = getRunInputsSnapshot();
        const res = await mutateGenerateSoilSightOutcomes(id ?? "");
        if (!isSameProject()) return;
        const outcomes: SoilSightOutcomesByScenario = {};
        Object.entries(res?.scenarios ?? {}).forEach(([scenarioId, scenarioOutcomes]) => {
          outcomes[scenarioId] = scenarioOutcomes?.years ?? [];
        });
        setSoilSightOutcomes(outcomes);
        setKeyInsight(res?.keyInsight ?? null);
        setCurrency(res?.currency ?? null);
        setHasCalculated(true);
        productAnalytics.track("soilsights:forecast_generate", {
          trigger,
          scenario_count: Object.keys(res?.scenarios ?? {}).length,
          timeline_years: timelineYearsRef.current,
        });
        // The results match the page only if every edit was saved and nothing changed while the run was out.
        const isPageUnchanged = getRunInputsSnapshot() === inputsAtRun && rerunRequestedEpochRef.current === null;
        if (isSaved && isPageUnchanged) setIsDirty(false);
        dirtyBeforeTabEditsRef.current.clear();
        firstCalculationSettledRef.current = true;
        settleLandingRecalc();
        queryClient.invalidateQueries({ queryKey: [GET_SCENARIO_RESILIENCE_SCORE] });
      } catch (error) {
        if (blockOnFieldReselect()) {
          // Fallback for any un-gated path: name the cause instead of the generic outcomes error.
          Message({ content: t("fieldReselect.calcBlocked"), icon: "error" });
        } else {
          trackForecastFail(classifyForecastError(error));
          // A coded API reason wins; only a genuine device-offline failure gets the offline message.
          Message({
            content: isDeviceOfflineError(error)
              ? t("errors.offline", { ns: "global" })
              : getUserFacingApiErrorMessage(error, t("outcomeComparison.soilSightsUnavailable")),
            icon: "error",
            dataTestId: "error-soil-sight-load-failed",
          });
        }
      } finally {
        setIsCalculating(false);
        recalculateInFlightRef.current = false;
        // Judged against the current project, not this run's: a run asked for after a switch still runs when the old project's run ends.
        const isRerunDue = rerunRequestedEpochRef.current === projectEpochRef.current;
        rerunRequestedEpochRef.current = null;
        if (isRerunDue) handleRecalculateRef.current(rerunTriggerRef.current).catch(() => {});
        else setIsSettingsRunPending(false);
      }
    },
    [
      blockOnFieldReselect,
      dirtyBeforeTabEditsRef,
      ensureProjectId,
      flushUnsavedChanges,
      getRunInputsSnapshot,
      isSavingRef,
      mutateGenerateSoilSightOutcomes,
      projectEpochRef,
      projectQuery.data?.locationRegion,
      projectQuery.isSuccess,
      queryClient,
      setAwaitingInitialCalc,
      setHasCalculated,
      setIsDirty,
      settleLandingRecalc,
      t,
    ]
  );

  const handleRecalculate = useCallback(
    async function handleRecalculate(trigger: ForecastTrigger) {
      setPendingRunRequests((count) => count + 1);
      try {
        await runRecalculation(trigger);
      } finally {
        setPendingRunRequests((count) => count - 1);
      }
    },
    [runRecalculation]
  );

  handleRecalculateRef.current = handleRecalculate;

  /** Resolves false when a guard stopped the run, so a caller can react to being blocked. */
  const runValidatedRecalculate = useCallback(
    async function runValidatedRecalculate({
      skipScrollToChart = false,
      trigger,
    }: {
      skipScrollToChart?: boolean;
      trigger: ForecastTrigger;
    }): Promise<boolean> {
      if (blockOnFieldReselect()) return false;
      setPendingRunRequests((count) => count + 1);
      try {
        const pruned = await pruneAndPersistEmptyYears(scenarios);
        if (!pruned) {
          trackForecastFail("save_failed");
          return false;
        }
        if (!passesValidation(pruned)) {
          trackForecastFail("incomplete_rotation");
          return false;
        }
        clearValidationErrors();
        if (!skipScrollToChart) requestChartScrollForManualRecalc(recalculateInFlightRef.current);
        // Started before this request is released, so the count never dips between validation and the run.
        handleRecalculate(trigger).catch(() => {});
        return true;
      } finally {
        setPendingRunRequests((count) => count - 1);
      }
    },
    [blockOnFieldReselect, clearValidationErrors, handleRecalculate, passesValidation, pruneAndPersistEmptyYears, requestChartScrollForManualRecalc, scenarios]
  );

  // Wrapped, not passed directly: this lands on onClick, which would supply a MouseEvent as options.
  const handleValidatedRecalculate = useCallback(
    async function handleValidatedRecalculate() {
      await runValidatedRecalculate({ trigger: "manual" });
    },
    [runValidatedRecalculate]
  );

  /** A settings-bar pick runs straight away and shows its own progress on the bar, without moving the page. */
  const runSettingsRecalculate = useCallback(
    function runSettingsRecalculate() {
      setIsSettingsRunPending(true);
      runValidatedRecalculate({ skipScrollToChart: true, trigger: "settings_change" })
        .then((started) => {
          if (!started) setIsSettingsRunPending(false);
        })
        .catch(() => setIsSettingsRunPending(false));
    },
    [runValidatedRecalculate]
  );

  /** A saved project-settings drawer runs at once and lands on the chart like a recalculate click. */
  const recalculateAfterSettingsSave = useCallback(
    async function recalculateAfterSettingsSave() {
      if (blockOnFieldReselect()) return;
      requestChartScrollForManualRecalc(recalculateInFlightRef.current);
      setIsSettingsRunPending(true);
      await handleRecalculate("project_settings");
    },
    [blockOnFieldReselect, handleRecalculate, requestChartScrollForManualRecalc]
  );

  const calculationsAreStale = useMemo(
    () => areOutcomesStale({ isDirty, isCalculating, isAwaitingInitialCalculation: awaitingInitialCalc, hasCalculated, soilSightOutcomes }),
    [isDirty, isCalculating, awaitingInitialCalc, hasCalculated, soilSightOutcomes]
  );

  const recalculateOnTabArrival = useCallback(
    function recalculateOnTabArrival() {
      return runValidatedRecalculate({ skipScrollToChart: true, trigger: "tab_arrival" });
    },
    [runValidatedRecalculate]
  );

  // We keep one stable callback per trigger, because callers pass them into effect-driven hooks.
  const recalculateOnProjectLoad = useCallback(
    function recalculateOnProjectLoad() {
      return handleRecalculate("project_load");
    },
    [handleRecalculate]
  );

  const recalculateAfterFieldReselect = useCallback(
    function recalculateAfterFieldReselect() {
      return runValidatedRecalculate({ trigger: "field_reselect" });
    },
    [runValidatedRecalculate]
  );

  // Drops the previous run's results, e.g. when the project's field changed.
  const clearOutcomes = useCallback(function clearOutcomes() {
    setSoilSightOutcomes(null);
    setKeyInsight(null);
    setCurrency(null);
  }, []);

  const hasAnyCompletedProjection = useMemo(() => getScenariosWithProjections(scenarios, soilSightOutcomes).length > 0, [scenarios, soilSightOutcomes]);

  // A language change re-runs so the AI key insights follow the new locale; not on mount, not during a run.
  const previousLanguageRef = useRef<string | null>(null);
  useEffect(
    function recalcOnLanguageChange() {
      if (previousLanguageRef.current === null) {
        previousLanguageRef.current = i18n.language;
        return;
      }
      if (previousLanguageRef.current === i18n.language) return;
      previousLanguageRef.current = i18n.language;
      if (!hasAnyCompletedProjection) return;
      if (recalculateInFlightRef.current) return;
      // Failures are already shown inside handleRecalculate.
      handleRecalculate("language_change").catch(() => {});
    },
    [i18n.language, hasAnyCompletedProjection, handleRecalculate]
  );

  return {
    isCalculating,
    isSettingsRunPending,
    isRunRequested: pendingRunRequests > 0,
    soilSightOutcomes,
    keyInsight: reconciledKeyInsight,
    currency,
    clearOutcomes,
    firstCalculationSettledRef,
    recalculateInFlightRef,
    isSavingCount,
    handleRecalculate,
    handleValidatedRecalculate,
    runSettingsRecalculate,
    recalculateAfterSettingsSave,
    calculationsAreStale,
    recalculateOnTabArrival,
    recalculateOnProjectLoad,
    recalculateAfterFieldReselect,
  };
};
